import {
  createFileRoute,
  Outlet,
  useNavigate,
  useRouterState,
  useSearch,
} from "@tanstack/react-router";
import { AnimatePresence, motion, type Variants } from "framer-motion";
import { useRef } from "react";
import { AddItemDrawer } from "@/components/AddItemDrawer";
import { AppBar } from "@/components/AppBar";
import { BottomNav } from "@/components/BottomNav";
import { ProfileMenu } from "@/components/ProfileMenu";
import { RecipeSearchBox } from "@/components/recipes/RecipeSearchBox";
import { StableOutlet } from "@/components/StableOutlet";

export const Route = createFileRoute("/_app/_shell")({
  component: ShellLayout,
});

const RECIPES_ROUTE_ID = "/_app/_shell/recipes";
const SHOPPING_ROUTE_ID = "/_app/_shell/shopping";

const popVariants: Variants = {
  initial: { opacity: 0, scale: 0.96 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.96 },
};

function ShellLayout() {
  const leafRouteId = useRouterState({
    select: (s) => s.matches.at(-1)?.routeId ?? "",
  });
  const isRecipesList = leafRouteId === RECIPES_ROUTE_ID;
  const isShoppingList = leafRouteId === SHOPPING_ROUTE_ID;

  const recipesSearch = useSearch({
    from: RECIPES_ROUTE_ID,
    shouldThrow: false,
  });
  const navigate = useNavigate();
  const mainRef = useRef<HTMLElement>(null);

  function setQuery(value: string) {
    navigate({
      to: "/recipes",
      search: (prev) => ({ ...prev, q: value || undefined }),
      replace: true,
    });
    // Reset scroll to the top as the filter changes: the results reorder by
    // relevance, so the best match belongs at the top of the viewport rather
    // than wherever the list happened to be scrolled to.
    mainRef.current?.scrollTo({ top: 0 });
  }

  return (
    <div className="flex h-full flex-col">
      <AppBar
        center={
          isRecipesList ? (
            <RecipeSearchBox
              value={recipesSearch?.q ?? ""}
              onChange={setQuery}
            />
          ) : (
            <h1 className="font-heading text-lg font-semibold tracking-tight">
              Provender
              {import.meta.env.VITE_APP_ENV !== "production" &&
                ` [${import.meta.env.VITE_APP_ENV}]`}
            </h1>
          )
        }
        right={<ProfileMenu />}
      />
      {/* isolate: keep z-indexed content (e.g. item date badges) in their own
          stacking context so they can't paint over the portaled AddItemDrawer */}
      <main
        ref={mainRef}
        className="relative isolate flex-1 overflow-y-auto overscroll-contain p-4"
      >
        <AnimatePresence initial={false} mode="popLayout">
          <motion.div
            key={leafRouteId}
            variants={popVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.18, ease: "easeOut" }}
          >
            <StableOutlet>
              <Outlet />
            </StableOutlet>
          </motion.div>
        </AnimatePresence>
      </main>
      <AddItemDrawer shouldShow={isShoppingList} />
      <BottomNav />
    </div>
  );
}
